import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { apiPrimeSlice } from 'app-redux/store';
import {
  CreatePersonModel,
  GetPersonModel,
  GetPersonWithRolesModel,
  ProblemDetails,
  SearchByEmailModel,
} from 'solstice-types';

const individualsApiSlice = apiPrimeSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIndividuals: builder.query<ProblemDetails[], void>({
      query: () => ({
        url: '/persons',
        method: 'GET',
      }),
      providesTags: ['Individuals'],
    }),

    getIndividualById: builder.query<GetPersonWithRolesModel, string>({
      query: (id) => ({
        url: `/persons/${id}`,
        method: 'GET',
      }),
      providesTags: ['IndividualsById'],
    }),

    addIndividual: builder.mutation<
      CreatePersonModel,
      {
        newIndividual: Partial<CreatePersonModel[]>;
        isGroup: boolean;
      }
    >({
      query: ({ newIndividual }) => ({
        url: '/persons',
        method: 'POST',
        body: newIndividual,
      }),
      invalidatesTags: (result, error, { isGroup }) => (isGroup ? ['Groups'] : ['Individuals']),
    }),

    searchIndividual: builder.mutation<GetPersonModel[], any>({
      query: (searchTerm) => ({
        url: '/persons/search',
        method: 'POST',
        body: searchTerm,
      }),
    }),

    searchIndividualByEmail: builder.mutation<GetPersonModel[], SearchByEmailModel>({
      query: (email) => ({
        url: '/persons/search-by-email',
        method: 'POST',
        body: email,
      }),
      transformResponse: (response: GetPersonModel | GetPersonModel[] | null) => {
        if (Array.isArray(response)) return response.filter(Boolean);
        return response && Object.keys(response).length > 0 ? [response] : [];
      },
    }),

    updateKycFlowStatus: builder.mutation<any, { id: string; status: string; note: string }>({
      query: ({ id, status, note }) => ({
        url: `/kyc-flows/${id}/status`,
        method: 'PUT',
        body: { status, note },
      }),
      invalidatesTags: ['IndividualsById', 'GetEntityById'],
    }),

    renewVerification: builder.mutation<any, string>({
      query: (personId) => ({
        url: `/kyc-flows/person/${personId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Individuals', 'IndividualsById', 'GetEntityById'],
    }),

    getVerificationResult: builder.query<string, string>({
      query: (kycFlowId) => ({
        url: `/kyc-flows/RefreshVerificationResult?kycFlowId=${kycFlowId}`,
        method: 'GET',
        responseHandler: 'text',
      }),
    }),

    getStatusReportPdf: builder.query<{ blob: Blob; filename: string }, string>({
      query: (kycFlowId) => ({
        url: `/kyc-flows/GetStatusReportPDF?kycFlowId=${kycFlowId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta) => {
        const response = (meta as FetchBaseQueryMeta | undefined)?.response;
        const contentDisposition = response?.headers.get('content-disposition') ?? '';
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        const filename = utf8Match
          ? decodeURIComponent(utf8Match[1])
          : (asciiMatch?.[1] ?? '');
        return { blob, filename };
      },
    }),
  }),
});

export const {
  useGetIndividualsQuery,
  useAddIndividualMutation,
  useSearchIndividualMutation,
  useGetIndividualByIdQuery,
  useSearchIndividualByEmailMutation,
  useUpdateKycFlowStatusMutation,
  useRenewVerificationMutation,
  useLazyGetVerificationResultQuery,
  useLazyGetStatusReportPdfQuery,
} = individualsApiSlice;
